import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { EmptyState } from "../../components/ui/EmptyState";
import { Skeleton } from "../../components/ui/Skeleton";
import { channelManager } from "../realtime/ChannelManager";
import { useSession } from "../auth/hooks";

import { useFavorites } from "./hooks";

export function FavoritesList() {
  const { t } = useTranslation();
  const { user, loading: sessionLoading, error: sessionError } = useSession();
  const authed = !!user;
  const query = useFavorites(authed);
  const items = query.data ?? [];
  const stationIdsKey = useMemo(
    () => (query.data ?? []).map((f) => f.stationId).sort().join(","),
    [query.data],
  );

  useEffect(() => {
    const ids = stationIdsKey ? stationIdsKey.split(",") : [];
    channelManager.subscribeToFavorites(ids);
    return () => channelManager.subscribeToFavorites([]);
  }, [stationIdsKey]);

  if (sessionLoading) {
    return (
      <div className="space-y-2" aria-busy="true">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }
  if (sessionError) {
    return (
      <EmptyState
        tone="ember"
        glyph="⚠"
        eyebrow={t("favorites.empty_eyebrow_error")}
        title={t("favorites.service_unavailable_long")}
        body={t("favorites.empty_error_body")}
      />
    );
  }
  if (!authed) {
    return (
      <EmptyState
        glyph="♡"
        eyebrow={t("favorites.empty_eyebrow_unauth")}
        title={t("favorites.signin_required")}
        body={t("favorites.empty_unauth_body")}
        action={
          <Link to="/auth/login" className="btn-volt inline-flex items-center px-4 py-2 text-sm">
            {t("favorites.signin_link")}
          </Link>
        }
      />
    );
  }
  if (query.isLoading) {
    return (
      <div className="space-y-2" aria-busy="true">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }
  if (query.isError) {
    return (
      <EmptyState
        tone="ember"
        glyph="⚠"
        eyebrow={t("favorites.empty_eyebrow_error")}
        title={t("favorites.load_failed")}
        body={t("favorites.empty_error_body")}
        action={
          <button
            type="button"
            onClick={() => query.refetch()}
            className="btn-ghost inline-flex items-center px-4 py-2 text-sm"
          >
            {t("common.retry")}
          </button>
        }
      />
    );
  }
  if (items.length === 0) {
    return (
      <EmptyState
        glyph="♡"
        eyebrow={t("favorites.empty_eyebrow")}
        title={t("favorites.empty")}
        body={t("favorites.empty_body")}
        action={
          <Link to="/" className="btn-volt inline-flex items-center px-4 py-2 text-sm">
            {t("favorites.empty_cta")}
          </Link>
        }
      />
    );
  }
  return (
    <ul className="space-y-2">
      {items.map((f) => (
        <li key={f.stationId}>
          <Link
            to={`/stations/${f.stationId}`}
            className="block rounded-md border border-line bg-surface p-3 hover:border-line-strong"
          >
            <p className="font-medium text-fg">
              {t("favorites.item_title", { prefix: f.stationId.slice(0, 8) })}
            </p>
            <p className="text-xs text-fg-mute">
              {t("favorites.saved_on", { date: f.savedAt.slice(0, 10) })}
            </p>
          </Link>
        </li>
      ))}
    </ul>
  );
}
